import { memo, useRef, useEffect } from "react";
import { useShallow } from "zustand/shallow";
import { useMapRouteStore } from "@/stores/mapRouteStore";
import SegmentationStep from "./segmentationStep";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

const SegmentationRoute = memo(function SegmentationRoute() {
  const {
    direction,
    selectedSegmentId,
    isRouteLoading,
    isNavigationMode,
    navigationStepIndex,
  } = useMapRouteStore(
    useShallow((s) => ({
      direction: s.direction,
      selectedSegmentId: s.selectedSegmentId,
      isRouteLoading: s.isRouteLoading,
      isNavigationMode: s.isNavigationMode,
      navigationStepIndex: s.navigationStepIndex,
    })),
  );

  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Автоскролл к текущему шагу в режиме навигации
  useEffect(() => {
    if (!isNavigationMode) return;

    const stepElement = stepRefs.current[navigationStepIndex];
    if (stepElement) {
      stepElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isNavigationMode, navigationStepIndex]);

  if (isRouteLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <LoadingSpinner size="base" />
        <span className="mt-3 text-gray-500 text-sm">
          Построение маршрута...
        </span>
      </div>
    );
  }

  if (!direction) return null;

  const segments = direction.segmentationRoute.segments;

  return (
    <div className="flex flex-col h-full">
      {/* Title - вне scroll */}
      <div className="px-4 pt-4 pb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 shrink-0">
        Маршрут
      </div>

      {/* Steps - scrollable */}
      <div
        className="flex-1 overflow-y-auto pb-5"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#c4c4c4 transparent",
        }}
      >
        <div className="relative">
          {/* Vertical line through all steps */}
          <div
            className="absolute w-[2px] bg-gray-200 rounded-full"
            style={{
              left: "30px",
              top: "16px",
              bottom: "16px",
            }}
          />

          {/* Segments */}
          {segments.map((s, i) => (
            <SegmentationStep
              key={s.id}
              ref={(el) => {
                stepRefs.current[i] = el;
              }}
              index={i}
              currentSegment={s}
              segments={segments}
              isActive={s.id === selectedSegmentId}
              isNavigationMode={isNavigationMode}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

export default SegmentationRoute;
